/**
 * Agents — the tenant's agent identities.
 *
 * The backend has no "all agents" endpoint; listing requires
 * `?organization_id=`, which the route supplies. Rows link into the same
 * workspace, so drilling in never leaves the tenant.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { listAgents } from '@optum/api-client/endpoints/agents';

import { useAuth } from '../../auth/AuthContext.js';
import { Button } from '../../ui/Button.js';
import { ListToolbar } from '../../ui/ListToolbar.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { qk } from '../../lib/query-keys.js';
import { useRequiredOrgId } from '../../lib/use-current-org.js';
import { DataTable } from '../../ui/DataTable.js';
import { ListShape } from '../../ui/shapes.js';

export function AgentsPage() {
  const { isPlatformAdmin } = useAuth();
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  const [search, setSearch] = useState('');
  // Scope comes from /orgs/:orgId. The in-page organization picker and its
  // empty state are gone: the workspace already answered that question, and
  // asking again on every scoped page is what made the tenant so easy to lose.
  const orgId = useRequiredOrgId();

  const agents = useQuery({
    queryKey: qk.agents(orgId),
    queryFn: () => listAgents({ organization_id: orgId }),
  });

  const rows = (agents.data ?? []).filter((a) =>
    a.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <ListShape
      title="Agents"
      subtitle="Actors that run tasks for this tenant. Each agent owns its own capability allow-list."
      action={
        isPlatformAdmin ? (
          <Button
            variant="primary"
            icon="add"
            onClick={() => panel.openForm({ name: 'new-agent', orgId })}
          >
            New agent
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        <ListToolbar bare search={search} onSearch={setSearch} placeholder="Search agents…" />

        {agents.isPending && <LoadingBlock />}
        {agents.isError && (
          <ErrorBlock
            message={agents.error instanceof Error ? agents.error.message : 'Failed to load'}
          />
        )}
        {agents.data && rows.length === 0 && (
          <EmptyBlock>
            {agents.data.length === 0
              ? 'No agents yet — create the first one.'
              : 'No agents match that search.'}
          </EmptyBlock>
        )}
        {rows.length > 0 && (
          <DataTable
            count={`${rows.length} of ${agents.data?.length ?? rows.length} agents`}
            hint="no org picker — the workspace is the scope"
            rows={rows}
            selectedKey={panel.current?.kind === 'agent' ? panel.current.id : undefined}
            onRowClick={(a) => panel.open({ kind: 'agent', id: a.id, orgId })}
            onRowOpenPage={(a) => openPage({ kind: 'agent', id: a.id, orgId })}
            columns={[
              {
                key: 'name',
                label: 'Agent',
                strong: true,
                wrap: true,
                render: (a) => (
                  <>
                    <div
                      style={{
                        fontSize: 'var(--text-body-md)',
                        fontWeight: 'var(--weight-medium)',
                        color: 'var(--text-strong)',
                      }}
                    >
                      {a.name}
                    </div>
                    {a.description && (
                      <div
                        style={{
                          marginTop: 5,
                          fontSize: 'var(--text-meta-lg)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {a.description}
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: 'runtime_state',
                label: 'Runtime',
                render: (a) => <StatusBadge status={a.runtime_state} />,
              },
              {
                key: 'capabilities',
                label: 'Capabilities',
                align: 'right',
                mono: true,
                render: (a) => a.capabilities.length,
              },
            ]}
          />
        )}
      </div>
    </ListShape>
  );
}
